<template>
  <div class="imageSelector" ref="imageSelector">
    <p v-if="selectAll" class="hint">{{ $t('CONFIGURATOR_CHOOSE_ALL_HINT_ONLY_INCLUDES_DISPLAYED') }}
      <HelpTooltip inline>{{ $t("CONFIGURATOR_CHOOSE_ALL_HINT") }}</HelpTooltip>
    </p>
    <p v-if="! isReadonly" class="hint">{{ selectHintText }}</p>
    <v-sheet v-if="hasFavoriteImages" elevation="0" class="imageSheet">
      <h3 class="sectionTitle">{{ $t("FAVORITE_IMAGES") }}</h3>
      <div class="imageLayout">
        <SelectableImagePreview v-for="image in favoriteImages"
                                :key="image.uniqueId"
                                class="imageSelectionDiv"
                                :image="image"
                                :filter="filter"
                                :selected="isSelected(image)"
                                @select="onSelect(image)"/>
      </div>
    </v-sheet>
    <v-sheet elevation="5" class="imageSheet">
      <h3 v-if="hasFavoriteImages || hasRecentlyUsedImages" class="sectionTitle">{{ $t("ALL_IMAGES") }}</h3>
      <VirtualScroll class="imageLayout"
                     :active="true"
                     :items="images"
                     :initial-items-count="8"
                     :preload-padding="137"
                     :visible-window="getContainer()"
                     ref="allImages"
      >
        <template scope="{item}">
          <SelectableImagePreview class="imageSelectionDiv"
                                  :image="item"
                                  :filter="filter"
                                  :selected="isSelected(item)"
                                  @select="onSelect(item)"/>
        </template>
      </VirtualScroll>
    </v-sheet>
  </div>
</template>

<script lang="ts">
import Component from 'vue-class-component'
import BasicComponent from "@/global/BasicComponent";
import {Prop, Watch} from "vue-property-decorator";
import {ConfiguredImageDto, ImageDto, ImageFilterDto} from "@swagger";
import ImagePreview from "@/components/ImagePreview.vue";
import SelectableImagePreview from "@/components/SelectableImagePreview.vue";
import HelpTooltip from "@/components/HelpTooltip.vue";
import VirtualScroll from "@/components/VirtualScroll.vue";

@Component({
  components: {VirtualScroll, HelpTooltip, SelectableImagePreview, ImagePreview}
})
export default class ImageSelector extends BasicComponent {

  @Prop([Array, Object]) value: ConfiguredImageDto | ConfiguredImageDto[]; // The selected imageSelection
  @Prop(Array) images: ImageDto[];
  @Prop(Array) recentlyUsedImages: ImageDto[];
  @Prop(Number) productId: number;
  @Prop(String) filter: ImageFilterDto;
  /** Optional, if the image is a detailed configuration for a given sub-product */
  @Prop(Boolean) multiple: boolean;
  /** Automatically selects all imageSelection; read-only */
  @Prop(Boolean) selectAll: boolean;
  @Prop(Boolean) dense: boolean; // When true, the imageSelection are shown only once, without an extra section for favorite and recent imageSelection
  /** Other single-download slots may already contain these photos. */
  @Prop({type: Array, default: () => []}) excludedDefaultImageIds: string[];
  /**
   * The number of parent references to go up in the DOM tree until the scrollable container
   */
  @Prop(Number) scrollableContainerParentLayers: number;

  isMounted: boolean = false;

  created() {
    if (this.selectAll) {
      this.selectAllImages();
    }
  }

  mounted() {
    this.isMounted = true;
    this.queueDefaultSelection();
  }

  beforeDestroy() {
    this.isMounted = false;
  }

  queueDefaultSelection(): void {
    // Let parent resets/prefills and all slot props settle before filling an empty slot.
    this.$nextTick(() => {
      if (!this.isMounted || this.isMultiple || this.value) {
        return;
      }
      const images = this.images.filter(image => !this.excludedDefaultImageIds.includes(image.uniqueId));
      // Parents already restrict this group to eligible codes, types and orientations.
      const recentIds: string[] = this.$store.getters.recentPhotoIds;
      const byId = new Map(images.map(image => [image.uniqueId, image]));
      const recent = recentIds.map(id => byId.get(id)).find(image => !!image);
      const image = recent ?? images.find(image => image.favorite) ?? images[0];
      if (image) {
        this.onSelect(image);
      }
    });
  }

  get selectHintText() {
    if (this.isMultiple) {
      return this.$t("CONFIGURATOR_HINT_CHOOSE_MULTIPLE");
    }
    else return this.$t("CONFIGURATOR_HINT_CHOOSE_ONE");
  }

  get favoriteImages(): ImageDto[] {
    return this.images.filter(image => image.favorite);
  }

  get hasFavoriteImages(): boolean {
    return !this.dense && !this.isReadonly && this.favoriteImages.length > 0;
  }

  get hasRecentlyUsedImages(): boolean {
    return !this.dense && !this.isReadonly && this.recentlyUsedImages && this.recentlyUsedImages.length > 0;
  }

  get isReadonly(): boolean {
    return this.selectAll;
  }

  get isMultiple(): boolean {
    return this.multiple || this.selectAll;
  }

  selectAllImages(): void {
    this.$emit("input", this.images.map(image => this.imageToConfiguredImage(image)));
  }

  isSelected(image: ImageDto): boolean {
    if (this.selectAll) {
      return true;
    }
    if (!this.isMultiple) {
      if (!this.value) {
        return false;
      }
      return (this.value as ConfiguredImageDto).uniqueImageId === image.uniqueId;
    }
    for (let i of (this.value as ConfiguredImageDto[])) {
      if (i.uniqueImageId === image.uniqueId) {
        return true;
      }
    }
    return false;
  }

  onSelect(image: ImageDto): void {
    if (this.isReadonly) {
      return;
    }
    if (this.isSelected(image)) {
      if (this.isMultiple) {
        this.deselect(image);
      }
    } else {
      this.$store.commit("rememberPhotoSelection", image.uniqueId);
      if (this.isMultiple) {
        (this.value as ConfiguredImageDto[]).push(this.imageToConfiguredImage(image));
      } else {
        this.$emit("input", this.imageToConfiguredImage(image));
      }
    }
  }

  imageToConfiguredImage(image: ImageDto): ConfiguredImageDto {
    return {
      uniqueImageId: image.uniqueId,
      imageId: image.id,
      imageCropId: image.imageCropId,
      productId: this.productId,
      filter: this.filter
    };
  }

  getContainer(): Element {
    if (! this.isMounted) {
      return null;
    }
    const parents = this.scrollableContainerParentLayers ?? 1;
    let el = this.$el;
    for (let i = 0; i<parents; i++) {
      el = el.parentElement;
    }
    return el;
  }

  onContainerSizeChange(): void {
    const allImagesScroller = this.$refs.allImages as VirtualScroll;
    console.log("Container size changed");
    if (! allImagesScroller) {
      return;
    }
    allImagesScroller.displayAdditionalItemsIfNecessary();
  }

  @Watch("multiple")
  onMultipleChange(): void {
    this.queueDefaultSelection();
  }

  @Watch("images")
  onImagesChange(images: ImageDto[], oldImages: ImageDto[]) {
    if (this.selectAll) {
      if (!oldImages || !this.imageArraysEqual(images, oldImages)) {
        this.selectAllImages();
      }
    }
    this.queueDefaultSelection();
  }

  @Watch("value")
  @Watch("excludedDefaultImageIds")
  onDefaultSelectionContextChange(): void {
    this.queueDefaultSelection();
  }

  @Watch("selectAll")
  onSelectAllChange() {
    if (this.selectAll) {
      this.selectAllImages();
    }
  }

  private imageArraysEqual(a: ImageDto[], b: ImageDto[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    const set = new Set(b.map(image => image.uniqueId));
    for (let image of a) {
      if (!set.has(image.uniqueId)) {
        return false;
      }
    }
    return true;
  }

  private deselect(image: ImageDto): void {
    const images = this.value as ConfiguredImageDto[];
    for (let i = 0; i < images.length; i++) {
      if (images[i].uniqueImageId === image.uniqueId) {
        images.splice(i, 1);
        return;
      }
    }
  }

}

</script>
<style lang="scss" scoped>
@use "../../stylesheets/Variables.scss" as *;

.imageSelector {
}

.hint {
  text-align: left;
  margin: 10px 15px 0 15px;
  font-style: italic;
}

.imageSheet {
  padding: 15px;
  margin-top: 10px;

  &:not(:last-of-type) {
    //margin-bottom: 25px;
  }
}

.imageLayout {
  display: flex;
  flex-wrap: wrap;
  padding-top: 5px;
  padding-bottom: 5px;
  align-items: center;
  justify-content: center;

  &:not(:last-of-type) {
    border-bottom: 1px solid $shop-selector-border;
  }
}

@media screen and (max-width: 599px) {
  .hint {
    margin-inline: 8px;
  }

  .imageSheet {
    padding-inline: 4px;
  }
}
</style>
<style lang="scss">

</style>
